import React, { useState, useEffect, useCallback } from 'react';
import { List } from 'lucide-react';

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function extractHeadings(content) {
  if (!content) return [];
  const headings = [];
  const lines = content.split('\n');
  for (const line of lines) {
    const h2 = line.match(/^## (.+)$/);
    const h3 = line.match(/^### (.+)$/);
    if (h3) {
      headings.push({ level: 3, text: h3[1].trim(), id: slugify(h3[1].trim()) });
    } else if (h2) {
      headings.push({ level: 2, text: h2[1].trim(), id: slugify(h2[1].trim()) });
    }
  }
  return headings;
}

export function TableOfContents({ content }) {
  const [activeId, setActiveId] = useState('');
  const headings = extractHeadings(content);

  const handleScroll = useCallback(() => {
    const offsets = headings
      .map(h => {
        const el = document.getElementById(h.id);
        if (!el) return null;
        return { id: h.id, top: el.getBoundingClientRect().top };
      })
      .filter(Boolean);

    let current = '';
    for (const { id, top } of offsets) {
      if (top <= 120) current = id;
    }
    setActiveId(current);
  }, [headings]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (headings.length < 3) return null;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <nav className="toc-container" data-testid="table-of-contents">
      <div className="flex items-center gap-2 mb-4">
        <List className="w-4 h-4 text-[#E63946]" />
        <h4
          className="text-white text-lg"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
        >
          In This Article
        </h4>
      </div>
      <ul className="toc-list">
        {headings.map((h) => (
          <li key={h.id} className={h.level === 3 ? 'toc-item toc-sub' : 'toc-item'}>
            <button
              onClick={() => scrollTo(h.id)}
              className={`toc-link ${activeId === h.id ? 'toc-active' : ''}`}
              data-testid={`toc-link-${h.id}`}
            >
              {h.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
